import React, {useRef} from 'react';
import {Link, useLocation, useOutletContext} from "react-router-dom";
import FlipMove from "react-flip-move";
import PositionTitle from "./PositionTitle";
import PositionTiming from "./PositionTiming";
import PositionJobSpecification from "./PositionJobSpecification";
import {usePositionForm} from "../../../providers/PositionFormProvider";
import {Empty} from "antd";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import {PictureOutlined} from "@ant-design/icons";
import NEWSkillFormButton from '../../profiles/components/NEWSkillFormButton';

const PositionFullForm = props => {

    const {
        project
    } = useProjectForm();
    const {
        formModel: position,
        position: originalModel,
        onFormSubmit,
        isProjectUpdating
    } = usePositionForm();
    const location = useLocation();
    const {
        isDisabled, goBack, onChange, isReadOnly
    } = useOutletContext();

    const formRef = useRef(null);


    const updatePositionItem = async (item, valueProp) => {

        let items = [...(position[valueProp] || [])];

        if (TB.isDefined(item.importance)) {
            if (TB.find(items, ['id', parseInt(item.id)])) {
                items = TB.map(items, model => {
                    if (model.id === parseInt(item.id)) {
                        return {...item};
                    }
                    return model;
                });
            } else {
                items.push(item);
            }
        } else {
            items = TB.xorBy(items, [item], 'id');
        }

        return items;
    }

    const selectSkill = async (skill) => {
        let skills = await updatePositionItem(skill, 'skills');
        if (!skills.length) {
            return TB.toast(
                `At least one skill is required!`,
                {'theme': 'danger'}
            )
        }
        return onChange({skills})
    }

    const selectIndustry = async (industry) => {
        let industries = await updatePositionItem(industry, 'industries');
        return onChange({industries})
    }

    const removeTemplate = async (template) => {
        if (position.templates.length === 1) {
            return TB.alert('warning', 'Wripple Role Required',
                'Heads up, you need at least one Wripple Role to search for matching talent. Click the <b>Manage Roles</b> button to search and select another role.', {title: 'Ok'})
        }

        let updates = await position.removeTemplateUpdate(template);

        if (updates) {
            await TB.wait(400);

            return onFormSubmit(updates)
        }
    }

    return (
        <div className="flex flex-col items-stretch gap-4">
            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <PositionTitle hideLabel={true} isReadOnly={isReadOnly}/>
            </div>
            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <div className="flex flex-row items-center self-stretch flex-nowrap gap-3">
                    <p className="flex-grow-0 flex-shrink-0 text-lg font-medium text-left text-[#3f434a]">
                        Timing & Budget
                    </p>
                </div>
                <PositionTiming isReadOnly={isReadOnly || position.isFixedFee() || project.isFixedFee()}/>
                {!isReadOnly && project.isFixedFee() && (
                    <Link to={{...location, pathname: 'timing-and-budget'}} state={location.state || {}}
                          className="flex-grow-0 flex-shrink-0 btn btn-link-primary py-1">
                        MANAGE DELIVERABLES
                    </Link>
                )}
            </div>
            <hr className="my-4"/>

            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <div className="flex flex-row items-center self-stretch flex-nowrap gap-3">
                    <p className="flex-grow-0 flex-shrink-0 text-lg font-medium text-left text-[#3f434a]">
                        Wripple {TB.pluralize('Role', position.templates?.length)}
                    </p>

                </div>
                <div className="flex flex-row flex-wrap gap-[16px]">
                    {TB.map(position.templates, template => (
                        <div key={`temp-${template.id}`}
                             className="flex justify-start items-center relative gap-2 px-3 py-2 bg-[#5ae399]">

                            <p className="text-xs font-medium text-left text-[#3f434a]">{template.name}</p>
                            {!isReadOnly && (<button
                                    type="button"
                                    disabled={isDisabled || isProjectUpdating}
                                    onClick={e => removeTemplate(template)}
                                    className="text-sm text-left text-[#3f434a]">
                                    <i className="fas fa-times"/>
                                </button>
                            )}
                        </div>
                    ))}

                    {!isReadOnly && (
                        <Link to={{...location, pathname: 'wripple-roles'}} state={location.state || {}}
                              className="flex-grow-0 flex-shrink-0 btn btn-link-primary py-1">
                            MANAGE ROLES
                        </Link>
                    )}
                </div>
            </div>
            <hr className="my-4"/>


            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <p className="self-stretch text-lg font-medium text-left text-[#3f434a]">
                    Skills Chosen
                </p>

                <div className="flex flex-col items-stretch relative">
                    <div className="discipline-skills-container skill-importance-container relative gap-3">
                        <FlipMove typeName={null} maintainContainerHeight={true}>
                            {TB.map((position.skills || []), (skill, index) => (
                                <div key={skill.id}>
                                    <NEWSkillFormButton
                                        valuePropName="importance"
                                        disabled={isDisabled || isProjectUpdating || isReadOnly}
                                        readonly={isReadOnly}
                                        scoringOptions={laravel.importance_levels}
                                        skill={skill}
                                        onChange={selectSkill}
                                        className="min-w-[115px]"
                                    />
                                </div>
                            ))}

                            {!isReadOnly && (
                                <Link className="skill add-btn self-center"
                                      key="add-more-skills-btn"
                                      to={{
                                          ...location,
                                          pathname: 'skills'
                                      }} state={location.state || {}}>

                                    <p>Add More Skills</p>

                                    <div className="s-divider"/>

                                    <i className={'fa fa-plus'}/>

                                </Link>
                            )}
                        </FlipMove>
                    </div>

                </div>

            </div>

            <hr className="my-4"/>

            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <p className="self-stretch text-lg font-medium text-left text-[#3f434a]">
                    Industry Experience Chosen
                </p>

                {position.industries?.length > 0 ? (
                    <div className="flex flex-col items-stretch relative">
                        <div className="discipline-skills-container skill-importance-container relative gap-3">
                            <FlipMove typeName={null} maintainContainerHeight={true}>
                                {TB.map((position.industries || []), (industry, index) => (
                                    <div key={industry.id}>
                                        <NEWSkillFormButton
                                            valuePropName="importance"
                                            disabled={isDisabled || isProjectUpdating || isReadOnly}
                                            readonly={isReadOnly}
                                            scoringOptions={laravel.importance_levels}
                                            skill={industry}
                                            onChange={selectIndustry}
                                            className="min-w-[115px]"
                                        />
                                    </div>
                                ))}
                                {!isReadOnly && (
                                    <Link className="skill add-btn self-center"
                                          key="add-more-industries-btn"
                                          to={{...location, pathname: 'industry-experience'}}
                                          state={location.state || {}}>

                                        <p>Add {position?.industries.length > 0 ? "More Industries" : "Industry"}</p>

                                        <div className="s-divider"/>

                                        <i className={'fa fa-plus'}/>

                                    </Link>
                                )}
                            </FlipMove>
                        </div>

                    </div>
                ) : (
                    <div className="self-stretch flex flex-col items-center justify-center">
                        <Empty description="No Industry Requirements" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                            <div className="flex flex-col items-center justify-center mt-2 mx-auto">
                                <Link className="skill add-btn self-center mr-0 ml-0"
                                      key="add-more-industries-btn"
                                      to={{...location, pathname: 'industry-experience'}}
                                      state={location.state || {}}>

                                    <p>Add Industries</p>

                                    <div className="s-divider"/>

                                    <i className={'fa fa-plus'}/>

                                </Link>
                            </div>
                        </Empty>
                    </div>
                )}

            </div>

            <hr className="my-4"/>
            <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
                <p className="self-stretch text-lg font-medium text-left text-[#3f434a]">
                    {/* Associated Brands */}
                    Desired Brand or Company Experience
                </p>

                {position.brands?.length > 0 ? (
                    <div className="flex flex-col items-stretch relative">
                        <div className="flex flex-row flex-wrap mt-2 gap-x-3 gap-y-5 mb-3">
                            {TB.map(position.brands || [],(brand, index) => {

                                return (
                                    <div key={`brand-${index}-${brand.id || brand.brandId}`}
                                         className="bg-gray-50 inline-flex flex-row justify-start items-center relative gap-3 py-3 px-4 border h-28">

                                        <div className="flex items-center justify-start">
                                            {brand.logo ? <img className="h-full max-h-20 w-full max-w-32 object-contain" src={brand.logo}
                                                               alt={brand.name}/> : (
                                                <PictureOutlined className="max-h-full max-w-full" style={{fontSize: 56}}/>
                                            )}
                                        </div>

                                        <div className="flex flex-col flex-grow gap-1">
                                        <p className="text-base">{brand.name || brand.domain}</p>
                                            {brand.name && brand.domain && (
                                                <p className="text-xs text-info whitespace-nowrap">
                                                    {brand.domain}
                                                </p>
                                            )}
                                        </div>

                                    </div>
                                )
                            })}
                            {!isReadOnly && (
                                <Link className="skill add-btn self-center"
                                      key="add-more-brands-btn"
                                      to={{...location, pathname: 'brands'}}
                                      state={location.state || {}}>

                                    <p>Manage Brands</p>

                                    {/*<div className="s-divider"/>*/}

                                    {/*<i className={'fa fa-plus'}/>*/}

                                </Link>
                            )}
                        </div>

                    </div>
                ) : (
                    <div className="self-stretch flex flex-col items-center justify-center">
                        <Empty description="No Associated Brand Requirements" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                            <div className="flex flex-col items-center justify-center mt-2 mx-auto">
                                <Link className="skill add-btn self-center mr-0 ml-0"
                                      key="add-more-brands-btn"
                                      to={{...location, pathname: 'brands'}}
                                      state={location.state || {}}>

                                    <p>Add Brands</p>

                                    <div className="s-divider"/>

                                    <i className={'fa fa-plus'}/>

                                </Link>
                            </div>
                        </Empty>
                    </div>
                )}

            </div>

            <hr className="my-4"/>

            <PositionJobSpecification isReadOnly={isReadOnly}/>

        </div>
    );
};

PositionFullForm.propTypes = {};

const styles = {
    fieldSectionCol: {
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    fieldSection: {
        flexDirection: 'row',
        display: 'flex',
        margin: '0 -15px',
        // flexWrap: 'wrap'
    },
    field: {
        padding: '0 15px',
    },
};
export default PositionFullForm;